import { FormEvent, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Breadcrumbs } from "../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../components/Breadcrumbs";
import { PageShell } from "../../shared/layouts/PageShell";
import { PageHeader } from "../../shared/ui/layouts/PageHeader";
import { SurfaceCard } from "../../shared/ui/surface/SurfaceCard";
import { FormFieldLabel } from "../../../components/FormFieldLabel";
import { useAuth } from "../../../providers/AuthProvider";
import { useToast } from "../../../providers/ToastProvider";
import { createWorkspace } from "../api/workspaces";
import { ApiError, getValidationErrors } from "../../../services/apiClient";

import { ArrowLeft, MapTrifold, Plus } from "@phosphor-icons/react";
import { defaultIconProps } from "../../shared/ui/icons";

const layoutGridClasses =
  "grid gap-6 items-start lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,1fr)] xl:gap-8";
const formStackClasses = "flex flex-col gap-6";
const fieldClasses = "flex flex-col gap-2";
const inputClasses =
  "block w-full rounded-xl border-0 py-2.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 disabled:bg-slate-50 disabled:text-slate-500 disabled:ring-slate-200 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700 dark:placeholder-slate-500 dark:disabled:bg-slate-800";
const ghostButtonClasses = "pm-btn pm-btn-ghost";
const primaryButtonClasses = "pm-btn pm-btn-primary";
const secondaryButtonClasses = "pm-btn pm-btn-secondary";

export function WorkspaceCreatePage() {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    return [
      { label: t("workspaces.my_workspaces.title"), to: "/workspaces" },
      { label: t("workspaces.create.title") },
    ];
  }, [t]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!accessToken) {
      return;
    }
    if (!name.trim()) {
      setFieldErrors({ name: t("workspaces.edit.name_required") });
      setError(t("workspaces.edit.name_required"));
      return;
    }
    setIsSaving(true);
    setError(null);
    setFieldErrors({});

    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
      };
      const workspace = await createWorkspace(accessToken, payload);
      toast.success(
        t("workspaces.my_workspaces.create_success", { name: workspace.name }),
      );
      navigate(`/workspaces/${workspace.id}/lots`, {
        state: { workspace },
        replace: true,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setFieldErrors(getValidationErrors(err));
        const message =
          err.body?.message ?? t("workspaces.my_workspaces.create_error");
        setError(message);
      } else {
        setFieldErrors({});
        setError(t("workspaces.my_workspaces.create_error"));
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PageShell width="wide">
      <div className="flex flex-col gap-8">
        <Breadcrumbs items={breadcrumbItems} />
        <PageHeader
          title={t("workspaces.create.title")}
          subtitle={t("workspaces.create.subtitle")}
          actions={
            <div className="flex flex-wrap items-center justify-end gap-3">
              <div className="flex items-center gap-3">
                <Link to="/workspaces" className={ghostButtonClasses}>
                  <ArrowLeft {...defaultIconProps} aria-hidden />
                  {t("actions.back")}
                </Link>
              </div>
            </div>
          }
        />

        {error ? (
          <div
            className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-300"
            role="alert"
          >
            {error}
          </div>
        ) : null}
        {Object.keys(fieldErrors).length > 0 ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100" role="alert">
            <ul className="list-disc pl-4">
              {Object.entries(fieldErrors).map(([field, message]) => (
                <li key={field}>{message}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className={layoutGridClasses}>
          <SurfaceCard as="section">
            <form className={formStackClasses} onSubmit={handleSubmit}>
              <div className={fieldClasses}>
                <label className="block" htmlFor="workspace-name">
                  <FormFieldLabel label={t("workspaces.edit.name_label")} />
                </label>
                <input
                  id="workspace-name"
                  type="text"
                  maxLength={255}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  required
                  disabled={isSaving}
                  className={inputClasses}
                  placeholder={t("workspaces.my_workspaces.placeholder_input")}
                />
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {t("workspaces.edit.name_hint")}
                </span>
              </div>

              <div className={fieldClasses}>
                <label className="block" htmlFor="workspace-description">
                  <FormFieldLabel
                    label={t("workspaces.edit.description_label")}
                    optional
                  />
                </label>
                <textarea
                  id="workspace-description"
                  maxLength={1000}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  disabled={isSaving}
                  rows={3}
                  className={inputClasses}
                  placeholder={t("workspaces.create.description_placeholder")}
                />
                <div className="text-right text-xs text-slate-500 dark:text-slate-400">
                  {t("workspaces.edit.description_counter", {
                    count: description.trim().length,
                    limit: 1000,
                  })}
                </div>
              </div>

              <SurfaceCard.Footer justify="end" gap="base" divider>
                <Link to="/workspaces" className={ghostButtonClasses}>
                  {t("actions.cancel")}
                </Link>
                <button
                  type="submit"
                  className={primaryButtonClasses}
                  disabled={isSaving || !name.trim()}
                >
                  {isSaving ? t("workspaces.my_workspaces.creating") : (
                    <>
                      <Plus {...defaultIconProps} aria-hidden />
                      {t("workspaces.my_workspaces.create_button")}
                    </>
                  )}
                </button>
              </SurfaceCard.Footer>
            </form>
          </SurfaceCard>

          <SurfaceCard as="section" tone="muted">
            <SurfaceCard.Body gap="base" textTone="default">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {t("workspaces.create.helper_title")}
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t("workspaces.create.helper_body")}
              </p>
              <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-300">
                <li className="flex items-start gap-2">
                  <span className="mt-1 inline-block h-2 w-2 rounded-full bg-sky-500" aria-hidden />
                  <span>{t("workspaces.my_workspaces.owned_description")}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 inline-block h-2 w-2 rounded-full bg-sky-500" aria-hidden />
                  <span>{t("workspaces.my_workspaces.shared_description")}</span>
                </li>
              </ul>
            </SurfaceCard.Body>
            <SurfaceCard.Footer justify="start" gap="base">
              <Link to="/workspaces" className={secondaryButtonClasses}>
                <ArrowLeft {...defaultIconProps} aria-hidden />
                {t("workspaces.my_workspaces.title")}
              </Link>
              <Link to="/workspaces/map" className={secondaryButtonClasses}>
                <MapTrifold {...defaultIconProps} aria-hidden />
                {t("workspaces.my_workspaces.all_lots_map_button")}
              </Link>
            </SurfaceCard.Footer>
          </SurfaceCard>
        </div>
      </div>
    </PageShell>
  );
}
